import type { ListBuilderConfiguration, ListBuilderList } from '../types';

/**
 * PD-16114 — Distribution → Schedule: approved orders with a delivery date
 * that are not on a trip yet, by day, overdue first ("what is on for
 * tomorrow, for Monday"). Merchandiser drop-offs, pick-ups and event rentals
 * are ordinary orders with a delivery date, so they wait here with every
 * other dated delivery.
 *
 * PD-16797 — open service orders are a second source on the same list
 * (`source: 'service'`, type Service): open, partly done and back for the
 * rest, and, with "include work already on a trip", scheduled ones.
 *
 * The rows come from a service (`ScheduleService` behind
 * GET /api/distribution/schedule), not a table: `objectTable:
 * 'distribution_schedule'` only scopes the field and filter keys. The day
 * labels ("Today", "Tomorrow", "Mon 10/12", "Overdue") are built on the
 * server from calendar dates, so no time zone can move a row to another day.
 */
const SOURCE_VALUE_MAPPING = [
	{ value: 'service', label: 'Service', color: { type: 'info' } },
];

const field = (fieldName: string) => ({
	fieldName,
	objectTable: 'distribution_schedule',
});

const isOrderRow = { field: 'source', condition: 'equals' as const, value: 'order' };
const isServiceRow = { field: 'source', condition: 'equals' as const, value: 'service' };

const defaultScheduleList: ListBuilderList = {
	id: 'default-distribution-schedule',
	name: 'Schedule',
	type: 'default',
	active: true,
	sortOrder: 1,
	// PD-16960 (F-32) — the server orders by day when no sort is sent; with no
	// default here the sort control read "Account (A-Z)" over rows in date
	// order.
	defaultSortId: 'schedule-date-asc',
	filterRules: {},
	columns: [
		{
			id: 'schedule-day',
			fields: [
				// "Today", "Tomorrow", "Overdue 3 days" (in red) or the weekday,
				// then the date once beneath it.
				{
					id: 'schedule-day-label',
					dataConfig: { type: 'field', field: field('dayLabel'), defaultValue: '-' },
					styleConfig: { size: 'md', weight: 'semibold', colorRule: { type: 'default' } },
					displayStyle: 'text',
					conditionalVisibility: { field: 'dayKind', condition: 'not_equals', value: 'overdue' },
				},
				{
					id: 'schedule-day-label-overdue',
					dataConfig: { type: 'field', field: field('dayLabel'), defaultValue: '-' },
					styleConfig: {
						size: 'md',
						weight: 'semibold',
						colorRule: { type: 'theme', themeColor: 'danger' },
					},
					displayStyle: 'text',
					conditionalVisibility: { field: 'dayKind', condition: 'equals', value: 'overdue' },
				},
				{
					id: 'schedule-day-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
				},
				{
					id: 'schedule-date-label',
					dataConfig: { type: 'field', field: field('dateLabel'), defaultValue: '-' },
					styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
					displayStyle: 'text',
				},
				{
					id: 'schedule-window-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
					conditionalVisibility: { field: 'windowLabel', condition: 'is_not_null' },
				},
				{
					id: 'schedule-window',
					dataConfig: { type: 'field', field: field('windowLabel') },
					styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
					displayStyle: 'text',
					conditionalVisibility: { field: 'windowLabel', condition: 'is_not_null' },
				},
			],
			label: 'Day',
			headers: [{ id: 'h-schedule-day', text: 'Day' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: true,
			sortFields: [{ direction: 'ASC', ...field('deliveryDate') }],
			// Fixed: "Overdue 12 days" and a delivery window must not be cut.
			width: 140,
		},
		{
			id: 'schedule-account',
			fields: [
				{
					id: 'schedule-client-name',
					dataConfig: { type: 'field', field: field('clientName'), defaultValue: '-' },
					link: { enabled: true, type: 'object', objectTable: 'client', idField: 'clientId' },
					styleConfig: { size: 'md', weight: 'medium', colorRule: { type: 'default' } },
					displayStyle: 'text',
				},
				{
					id: 'schedule-account-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
				},
				{
					id: 'schedule-address',
					dataConfig: { type: 'field', field: field('addressLabel'), defaultValue: 'No address' },
					styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
					displayStyle: 'text',
				},
			],
			label: 'Account',
			headers: [{ id: 'h-schedule-account', text: 'Account' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: true,
			sortFields: [{ direction: 'ASC', ...field('clientName') }],
			minWidth: 200,
			widthConfig: 'large',
		},
		{
			id: 'schedule-work',
			fields: [
				// PD-16797 — a service order says so before its lines.
				{
					id: 'schedule-service-badge',
					dataConfig: { type: 'field', field: field('source') },
					styleConfig: { size: 'sm', valueMapping: SOURCE_VALUE_MAPPING },
					displayStyle: 'badge',
					conditionalVisibility: isServiceRow,
				},
				{
					id: 'schedule-summary',
					dataConfig: { type: 'field', field: field('summary'), defaultValue: '-' },
					styleConfig: { size: 'md', colorRule: { type: 'default' } },
					displayStyle: 'text',
				},
				{
					id: 'schedule-event-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
					conditionalVisibility: { field: 'eventLabel', condition: 'is_not_null' },
				},
				{
					id: 'schedule-event',
					dataConfig: { type: 'field', field: field('eventLabel') },
					styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
					displayStyle: 'text',
					conditionalVisibility: { field: 'eventLabel', condition: 'is_not_null' },
				},
			],
			label: 'Work',
			headers: [{ id: 'h-schedule-work', text: 'Work' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: false,
			minWidth: 200,
			widthConfig: 'large',
		},
		{
			id: 'schedule-order',
			fields: [
				{
					id: 'schedule-order-number',
					dataConfig: { type: 'field', field: field('orderNumber'), defaultValue: '-' },
					link: {
						enabled: true,
						type: 'custom',
						urlTemplate: '/orders/{orderId}',
					},
					styleConfig: { size: 'md', colorRule: { type: 'default' } },
					displayStyle: 'text',
					conditionalVisibility: isOrderRow,
				},
				// PD-16797 — a service order opens the customer's Service tab,
				// where its lines and history are.
				{
					id: 'schedule-service-number',
					dataConfig: { type: 'field', field: field('orderNumber'), defaultValue: '-' },
					link: {
						enabled: true,
						type: 'custom',
						urlTemplate: '/clients/{clientId}/service',
					},
					styleConfig: { size: 'md', colorRule: { type: 'default' } },
					displayStyle: 'text',
					conditionalVisibility: isServiceRow,
				},
			],
			label: 'Order',
			headers: [{ id: 'h-schedule-order', text: 'Order' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: false,
			// Fixed widths on the short columns (order, total, trip, actions)
			// leave the rest to Account and Work; at 1280 wide the flexible
			// presets overran the table and the row buttons covered Trip.
			width: 100,
		},
		{
			id: 'schedule-total',
			fields: [
				{
					id: 'schedule-total-value',
					dataConfig: { type: 'field', field: field('total'), defaultValue: '0' },
					styleConfig: { size: 'md', colorRule: { type: 'default' } },
					displayStyle: 'currency',
					// A service order is not a sale; it has no total.
					conditionalVisibility: isOrderRow,
				},
			],
			label: 'Total',
			headers: [{ id: 'h-schedule-total', text: 'Total' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: true,
			sortFields: [{ direction: 'DESC', ...field('total') }],
			width: 100,
		},
		{
			id: 'schedule-trip',
			fields: [
				{
					id: 'schedule-trip-name',
					dataConfig: { type: 'field', field: field('tripName'), defaultValue: 'Not on a trip' },
					link: {
						enabled: true,
						type: 'custom',
						urlTemplate: '/distribution/trip/{tripId}',
					},
					styleConfig: { size: 'sm', colorRule: { type: 'default' } },
					displayStyle: 'text',
					conditionalVisibility: { field: 'tripId', condition: 'is_not_null' },
				},
				{
					id: 'schedule-trip-none',
					dataConfig: { type: 'custom_text', customText: 'Not on a trip' },
					styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
					displayStyle: 'text',
					conditionalVisibility: { field: 'tripId', condition: 'is_null' },
				},
			],
			label: 'Trip',
			headers: [{ id: 'h-schedule-trip', text: 'Trip' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: false,
			width: 140,
		},
		{
			// Moving or cancelling a merchandiser drop-off or pick-up (an
			// event's two orders move and cancel together). Every action sits
			// in the standard row menu: inline buttons did not fit the column
			// and spilled over Trip.
			id: 'schedule-actions',
			headers: [{ id: 'h-schedule-actions', text: '' }],
			fields: [
				{
					id: 'schedule-actions-dropdown',
					dataConfig: { type: 'custom_text', customText: '' },
					styleConfig: { size: 'md', colorRule: { type: 'default' } },
					displayStyle: 'actions_dropdown',
					dropdownActions: [
						{
							id: 'schedule-reschedule',
							visibility: 'overflow',
							label: 'Change date',
							iconName: 'calendar',
							callbackName: 'rescheduleEquipment',
							visibilityCallback: 'isEquipmentRow',
							requiredPermission: 'distribution:equipment_orders',
						},
						{
							id: 'schedule-cancel',
							visibility: 'overflow',
							label: 'Cancel',
							iconName: 'xmark',
							callbackName: 'cancelEquipment',
							visibilityCallback: 'isEquipmentRow',
							requiredPermission: 'distribution:equipment_orders',
							danger: true,
						},
						// PD-16797 — a service order: edit (date, lines,
						// technician) or cancel it.
						{
							id: 'schedule-service-edit',
							visibility: 'overflow',
							label: 'Edit service order',
							iconName: 'pen',
							callbackName: 'editServiceOrder',
							visibilityCallback: 'isServiceRow',
							requiredPermission: 'service_orders:manage',
						},
						{
							id: 'schedule-service-cancel',
							visibility: 'overflow',
							label: 'Cancel service order',
							iconName: 'xmark',
							callbackName: 'cancelServiceOrder',
							visibilityCallback: 'isServiceRow',
							requiredPermission: 'service_orders:manage',
							danger: true,
						},
					],
				},
			],
			label: 'Actions',
			sortable: false,
			width: 60,
			alignment: 'center',
			verticalAlignment: 'middle',
		},
	],
};

export const DISTRIBUTION_SCHEDULE_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [defaultScheduleList],
	tableSorts: [
		{
			id: 'schedule-date-asc',
			name: 'Date (overdue first)',
			description: 'Overdue first, then each day in order',
			fields: [{ direction: 'ASC', ...field('deliveryDate') }],
		},
		{
			id: 'schedule-client-asc',
			name: 'Account (A-Z)',
			fields: [{ direction: 'ASC', ...field('clientName') }],
		},
	],
	showResetButton: true,
	filters: {
		activeFilters: ['scheduleType', 'scheduleIncludeOnTrip'],
	},
	objects: [
		{
			baseUrl: '/orders/:orderId',
			objectLabel: 'Scheduled order',
			objectTable: 'distribution_schedule',
			selectedFields: [
				{ fieldName: 'dayLabel', fieldLabel: 'Day' },
				{ fieldName: 'dateLabel', fieldLabel: 'Date' },
				{ fieldName: 'windowLabel', fieldLabel: 'Window' },
				{ fieldName: 'clientName', fieldLabel: 'Account' },
				{ fieldName: 'addressLabel', fieldLabel: 'Address' },
				{ fieldName: 'summary', fieldLabel: 'Work' },
				{ fieldName: 'orderNumber', fieldLabel: 'Order' },
				{ fieldName: 'source', fieldLabel: 'Source' },
				{ fieldName: 'total', fieldLabel: 'Total' },
				{ fieldName: 'tripName', fieldLabel: 'Trip' },
			],
		},
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [],
		},
	],
};
